import * as React from "react";
import { Box, IconButton, Tooltip, alpha, useTheme } from "@mui/material";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import ReplayIcon from "@mui/icons-material/Replay";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useSocketConnection } from "./useSocketConnection";

export function SocketConnectionBadge() {
  const theme = useTheme();
  const {
    status,
    attempts,
    lastError,
    connect,
    retry,
    disconnect,
  } = useSocketConnection();

  const isPending = status === "connecting" || status === "reconnecting";

  // Premium Color Mapping
  const dotColor =
    status === "connected" ? theme.palette.success.main
    : isPending ? theme.palette.warning.main
    : theme.palette.error.main;

  const tooltip =
    status === "connected"
      ? "Socket: Connected"
      : status === "connecting"
      ? "Socket: Connecting…"
      : status === "reconnecting"
      ? `Socket: Reconnecting (attempt ${attempts})`
      : lastError
      ? `Socket: Disconnected — ${lastError}`
      : "Socket: Disconnected";

  // Premium Button Styling
  const btnSx = { 
    p: 0.5, 
    transition: 'all 0.2s',
    "& .MuiSvgIcon-root": { fontSize: 16 },
    "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.08) },
    "&.Mui-disabled": { opacity: 0.3 }
  };

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.5,
        bgcolor: alpha(theme.palette.text.primary, 0.03),
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: "12px",
        height: 32,
        transition: "all 0.3s ease",
        "&:hover": {
          bgcolor: alpha(theme.palette.text.primary, 0.05),
          borderColor: alpha(dotColor, 0.3),
        }
      }}
    >
      {/* status dot with Aura */}
      <Tooltip title={tooltip} arrow placement="bottom">
        <Box
          sx={{
            position: "relative",
            width: 10,
            height: 10,
            mr: 0.5,
            borderRadius: "50%",
            bgcolor: dotColor,
            boxShadow: `0 0 8px ${alpha(dotColor, 0.6)}`,
            ...(isPending && {
              animation: "premiumPulse 1.8s ease-in-out infinite",
            }),
            "@keyframes premiumPulse": {
              "0%": { transform: "scale(1)", opacity: 1, boxShadow: `0 0 0px ${alpha(dotColor, 0)}` },
              "50%": { transform: "scale(1.2)", opacity: 0.7, boxShadow: `0 0 10px ${alpha(dotColor, 0.8)}` },
              "100%": { transform: "scale(1)", opacity: 1, boxShadow: `0 0 0px ${alpha(dotColor, 0)}` },
            },
          }}
        />
      </Tooltip>

      {/* Action Group */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        {/* connect */}
        <Tooltip title="Connect" arrow>
          <span>
            <IconButton
              size="small"
              onClick={connect}
              disabled={status === "connected" || isPending}
              sx={{ ...btnSx, color: theme.palette.success.main }}
              aria-label="Connect socket"
            >
              <PowerSettingsNewIcon />
            </IconButton>
          </span>
        </Tooltip>

        {/* retry */}
        <Tooltip title="Retry" arrow>
          <span>
            <IconButton
              size="small"
              onClick={retry}
              disabled={status === "connected"}
              sx={{ 
                ...btnSx, 
                color: theme.palette.warning.main,
                ...(isPending && {
                    animation: "spin 2s linear infinite",
                }),
                "@keyframes spin": {
                    "0%": { transform: "rotate(0deg)" },
                    "100%": { transform: "rotate(360deg)" },
                }
              }}
              aria-label="Retry socket"
            >
              <ReplayIcon />
            </IconButton>
          </span>
        </Tooltip>

        {/* disconnect */}
        <Tooltip title="Disconnect" arrow>
          <span>
            <IconButton
              size="small"
              onClick={disconnect}
              disabled={status === "disconnected"}
              sx={{ ...btnSx, color: theme.palette.error.main }}
              aria-label="Disconnect socket"
            >
              <LinkOffIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}